<template>
  <lf-card
    :has-header-margin="false"
    :has-header-divider="showHeader"
    :has-header-padding="showHeader"
    class="mb-8 pt-6"
  >
    <template #header>
      <lf-h3>{{ $t("DEALS.APPLICATION_LINKS.TITLE") }}</lf-h3>
    </template>
    <slot name="supplemental" />
    <generate-link
      v-for="(link, index) in dealLinks"
      :key="index"
      :data="link"
      :application-data="props.applicationData"
      :show-legacy="showLegacy"
      :class="{ 'border-t': index > 0 }"
    />
    <div
      v-if="auth.isIgnisClient"
      class="flex items-center justify-end border-t py-6 pr-4"
    >
      <div class="right-col flex items-center space-x-2">
        <lf-dropdown
          v-if="!showLegacy"
          class="min-w-80"
          :value="selectedContractTemplate"
          name="workflow"
          :placeholder="$t('CONTRACTS.CONTRACT_BUILDER.TEMPLATE')"
          :options="templateDropdownOptions"
          full-width
          data-cy="workflow-dropdown"
          @change="selectedContractTemplate = $event"
        />
        <primary-button @click="sendIgnisContract">
          <loader :is-loading="loading" background-color="initial" />
          <span :class="{ 'text-transparent': loading }">
            {{ $t("COMMON.SEND") }}
            {{ $t("COMMON.FUNDING") }}
            {{ $t("CONTRACTS.CONTRACT") }}
          </span>
        </primary-button>
      </div>
    </div>
  </lf-card>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import compact from "lodash/compact";

import { APPLICATION_LINK_TYPE_CONTRACT } from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useContractBuilder } from "@/hooks/contractBuilder";

import IconCodatLarge from "@/components/icons/IconCodatLarge.vue";
import IconPlaidLarge from "@/components/icons/IconPlaidLarge.vue";
import IconResumeApp from "@/components/icons/IconResumeApp.vue";
import GenerateLink from "@/views/deals/components/GenerateLink.vue";

import type { IApplication } from "@/models/applications";
import type { IGenerateLinkData } from "@/models/common";

const props = withDefaults(
  defineProps<{
    applicationData: IApplication;
    showLegacy?: boolean;
    showHeader?: boolean;
  }>(),
  { showLegacy: false, showHeader: false }
);

const auth = useAuth();
const { t } = useI18n();
const { dispatch } = useStore();
const { templateDropdownOptions } = useContractBuilder();

const selectedContractTemplate = ref<string | null>(null);

const docusignLivenessEnabled = computed(
  () => !!props.applicationData.client?.has_docusign_liveness
);

const dealLinks = computed<IGenerateLinkData[]>(() =>
  compact([
    {
      icon: IconPlaidLarge,
      title: t("DEALS.APPLICATION_LINKS.PLAID"),
      description: t("DEALS.APPLICATION_LINKS.PLAID_DESCRIPTION"),
      action: "applications/generatePlaidLink",
      linkData: {
        application_id: props.applicationData.id,
      },
    },
    {
      icon: IconResumeApp,
      title: t("DEALS.APPLICATION_LINKS.RESUME_APP"),
      description: t("DEALS.APPLICATION_LINKS.RESUME_APP_DESCRIPTION"),
      action: "applications/getResumeAppLink",
      showSelectWidget: true,
      showConsentTemplate: true,
      type: APPLICATION_LINK_TYPE_CONTRACT,
      linkData: {
        application_id: props.applicationData.id,
      },
    },
    docusignLivenessEnabled.value && {
      icon: IconResumeApp,
      title: t("DEALS.APPLICATION_LINKS.CONTRACT_WITH_IDV"),
      description: t("DEALS.APPLICATION_LINKS.IDV_DESCRIPTION"),
      action: "applications/getLivenessLink",
      showConsentTemplate: false,
      type: APPLICATION_LINK_TYPE_CONTRACT,
      linkData: {
        application_id: props.applicationData.id,
      },
    },
    docusignLivenessEnabled.value && {
      icon: IconResumeApp,
      title: t("DEALS.APPLICATION_LINKS.CONTRACT_WITH_IDV"),
      description: t("DEALS.APPLICATION_LINKS.IDV_CHECK_DESCRIPTION"),
      action: "applications/getLivenessLink",
      showConsentTemplate: false,
      type: APPLICATION_LINK_TYPE_CONTRACT,
      linkData: {
        application_id: props.applicationData.id,
        internal: 1,
      },
    },
    {
      icon: IconCodatLarge,
      title: t("DEALS.APPLICATION_LINKS.ACCOUNTING_CONNECTION"),
      description: t("DEALS.APPLICATION_LINKS.CODAT_LINK"),
      action: "applications/generateCodatWidgetLink",
      linkData: {
        application_id: props.applicationData.id,
      },
    },
    {
      icon: IconResumeApp,
      title: t("DEALS.APPLICATION_LINKS.BANK_STATEMENTS"),
      description: t("DEALS.APPLICATION_LINKS.MANUAL_UPLOAD_LINK"),
      action: "applications/getBankStatementLink",
      linkData: {
        application_id: props.applicationData.id,
      },
    },
  ])
);

const { fetchWrapper: sendIgnisContract, loading } = usePromiseWrapper(() =>
  dispatch("applications/sendFundingContract", {
    applicationId: props.applicationData.id,
    contractTemplateId: props.showLegacy
      ? undefined
      : (selectedContractTemplate.value ?? undefined),
  })
);
</script>
